import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { useRoute, useNavigation, useFocusEffect, RouteProp, NavigationProp } from '@react-navigation/native';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { PageContainer, Navbar, Breadcrumb, ActivityCalendar } from '../../components';
import ProjectHeader from './components/ProjectHeader';
import DetailStatsRow from './components/DetailStatsRow';
import KPIsSection from './components/KPIsSection';
import ActivitiesSection from './components/ActivitiesSection';
import OutputsSection from './components/OutputsSection';
import DevelopmentPlan from './components/DevelopmentPlan';
import DetailTeachersTable from './components/DetailTeachersTable';
import StudentsTable from './components/StudentsTable';
import TalentsSkeleton from './components/TalentsSkeleton';
import { talentProjectsApi, talentActivitiesApi } from '../../services/apiService';
import { API_BASE_HOST } from '../../constants/config';
import { ProjectDetailData } from './types';
import { CalendarEvent } from '../../components/ActivityCalendar/types';
import { MainStackParamList } from '../../navigation/MainNavigator';

type ScreenRouteProp = RouteProp<MainStackParamList, 'ProjectDetail'>;
type ScreenNavProp = NavigationProp<MainStackParamList>;

export default function ProjectDetailScreen() {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<ScreenNavProp>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { user } = useAuth();
  const isCoordinator = user?.profile?.user_type === 'coordinator';
  const readOnly = user?.profile?.user_type === 'strategy_team';
  const isStudent = user?.profile?.user_type === 'student';
  const noCreate = readOnly || isCoordinator || isStudent;
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const projectIdParam = route.params?.id;
  const projectName = route.params?.name ? decodeURIComponent(route.params.name) : '';
  const entityName = route.params?.entityName;
  const [projectId, setProjectId] = useState<number | undefined>(projectIdParam);

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<ProjectDetailData>({
    id: 0, title: '', dateRange: '', description: '', weight: 0,
    studentsCount: 0, teachersCount: 0, kpisCount: 0, activitiesCount: 0, sessionsCount: 0,
    overallProgress: 0, statusDistribution: { excellent: 0, good: 0, needsImprovement: 0 },
    kpis: [], activities: [], outputs: [],
    sustainabilityPlan: { title: '', content: '' },
  });
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [planFileName, setPlanFileName] = useState('');

  useFocusEffect(useCallback(() => {
    const fetchData = async () => {
      setIsLoading(true);

      // If we don't have an id (deep link by name), resolve it first
      let resolvedId = projectId;
      if (!resolvedId && projectName) {
        try {
          const list = await talentProjectsApi.list();
          const projects = list?.results || list;
          if (Array.isArray(projects)) {
            const match = projects.find((p: any) => p.name === projectName);
            if (match) {
              resolvedId = match.id;
              setProjectId(match.id);
            }
          }
        } catch (e) {
          console.log('[ProjectDetail] Failed to resolve name to id:', e);
        }
      }
      if (!resolvedId) {
        setIsLoading(false);
        return;
      }

      // Fetch project details
      try {
        const resp = await talentProjectsApi.get(resolvedId);
        if (resp) {
          const transformed: ProjectDetailData = {
            id: resp.id,
            title: resp.name || '',
            dateRange: resp.date_range || '',
            description: resp.description || '',
            weight: resp.weight ?? 0,
            specializationId: resp.specialization || undefined,
            studentsCount: resp.students_count ?? 0,
            teachersCount: resp.teachers_count ?? resp.teachers?.length ?? 0,
            kpisCount: resp.kpis_count ?? resp.kpi_links?.length ?? 0,
            activitiesCount: resp.activities_count ?? resp.activities?.length ?? 0,
            sessionsCount: resp.sessions_count ?? 0,
            overallProgress: resp.overall_progress ?? resp.progress_percentage ?? 0,
            statusDistribution: resp.status_distribution ?? { excellent: 0, good: 0, needsImprovement: 0 },
            kpis: (resp.kpi_links || []).map((k: any) => ({
              id: k.kpi || k.id,
              linkId: k.id,
              name: k.kpi_name || k.name || '',
              targetValue: k.target_value ?? 0,
              achievedValue: k.achieved_value ?? 0,
              unit: k.unit || 'count',
              isAchieved: (k.achieved_value ?? 0) >= (k.target_value ?? 0),
            })),
            activities: (resp.activities || []).map((a: any) => ({
              id: a.id,
              name: a.name || '',
              progress: a.progress_percentage ?? 0,
            })),
            outputs: (resp.outputs || []).map((o: any) => ({
              id: o.id,
              title: o.title || '',
              image: (() => { const src = o.thumbnail || o.file; return src ? (src.startsWith('http') ? src : `${API_BASE_HOST}${src}`) : undefined; })(),
              date: o.created_at || undefined,
            })),
            sustainabilityPlan: resp.sustainability_plan ?? { title: '', content: '' },
          };
          setData(transformed);
          if (resp.plan_file) setPlanFileName(resp.plan_file.split('/').pop() || '');
        }
      } catch (e) {
        console.log('[ProjectDetail] API failed:', e);
      }
      // Fetch calendar events
      try {
        const events = await talentActivitiesApi.calendar({ project: resolvedId });
        if (Array.isArray(events) && events.length > 0) {
          setCalendarEvents(events);
        }
      } catch (e) {
        console.log('[ProjectDetail] Calendar API failed:', e);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [projectId, projectName]));

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.talents'), route: 'Talents' },
              // Clickable parent-specialization crumb when we know it.
              ...(entityName
                ? [{ label: entityName, route: 'SpecializationDetail', params: { name: entityName, id: data?.specializationId } }]
                : []),
              { label: isRTL ? 'تفاصيل المشروع' : 'Project Details' },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              {/* Section 1: Project Header */}
              <ProjectHeader
                title={data.title || projectName}
                dateRange={data.dateRange}
                description={data.description}
                weight={data.weight}
                entityName={entityName}
                onEdit={readOnly || isStudent ? undefined : () => navigation.navigate('AddProject', { specializationId: data.specializationId || 1, projectId })}
              />

              {/* Section 2: Stats Row */}
              <DetailStatsRow
                studentsCount={data.studentsCount}
                teachersCount={data.teachersCount}
                activitiesCount={data.activitiesCount}
                kpisCount={data.kpisCount}
                programsCount={data.activitiesCount}
                sessionsCount={data.sessionsCount}
                overallProgress={data.overallProgress}
                statusDistribution={data.statusDistribution}
              />

              {/* Section 3: KPIs — hidden for students */}
              {!isStudent && (
                <KPIsSection kpis={data.kpis} readOnly={noCreate} onAddKPI={() => navigation.navigate('AddTalentKPI', { projectId })} onViewKPI={(kpiLinkId) => navigation.navigate('TalentKPIDetail' as any, { kpiLinkId })} />
              )}

              {/* Section 4: Activities */}
              <ActivitiesSection
                activities={data.activities}
                readOnly={noCreate}
                onViewActivity={(id) => navigation.navigate('TalentActivityDetail', { id, entityName })}
                onAddActivity={noCreate ? undefined : () => navigation.navigate('AddTalentActivity', { projectId, specializationId: data.specializationId })}
              />

              {/* Section 5: Calendar */}
              <ActivityCalendar
                events={calendarEvents}
                onEventPress={(event) => navigation.navigate('TalentActivityDetail', { id: event.id, entityName })}
                onNewActivity={noCreate ? undefined : () => navigation.navigate('AddTalentActivity', { projectId, specializationId: data.specializationId })}
              />

              {/* Section 6: Outputs */}
              <OutputsSection outputs={data.outputs} readOnly={noCreate} projectId={projectId} />

              {/* Section 7: Teachers Table — hidden for coordinators and students */}
              {!isCoordinator && !isStudent && <DetailTeachersTable projectId={projectId} readOnly={readOnly} specializationId={data.specializationId} entityName={entityName} />}

              {/* Section 8: Students Table — hidden for students */}
              {!isStudent && <StudentsTable readOnly={readOnly} projectId={projectId} specializationId={data.specializationId} entityName={entityName} />}

              {/* Section 9: Sustainability Plan — hidden for students */}
              {!isStudent && (
                <DevelopmentPlan
                  plan={data.sustainabilityPlan}
                  titleOverride={isRTL ? 'خطة متابعة وتنمية الطلبة' : 'Student Development Plan'}
                  entityId={projectId}
                  entityType="project"
                  planFileName={planFileName}
                  readOnly={isCoordinator}
                />
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  sections: {
    gap: 20,
  },
});
